import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  Heart,
  Target,
  Users,
  Globe,
  BookOpen,
  FileText,
  Lightbulb,
  Award,
  Gavel,
  GraduationCap,
  CheckCircle,
  Star,
  Eye,
  MapPin,
  Calendar,
  Briefcase,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Mail
} from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  // Founder information for SEO - Enhanced with more semantic data
  const founderInfo = {
    name: "Quainoo Emmanuel Asiedu Newton",
    alternateName: ["Emmanuel Asiedu Newton", "Quainoo Emmanuel", "Emmanuel Newton Quainoo", "Quainoo Newton", "E.A. Newton", "Emmanuel Newton"],
    birthDate: "1980-03-27",
    birthPlace: "Ghana",
    jobTitle: "Founder & CEO",
    occupation: "Teaching Professional & EdTech Entrepreneur",
    image: "https://customer-assets.emergentagent.com/job_doc-generator-app/artifacts/i7q5z79x_1766305356486.jpg",
    organizations: ["EduLegalHub", "Asomas", "Court Connect"],
    education: [
      { name: "Koforidua Technical University", type: "CollegeOrUniversity", degree: "Technical Education" },
      { name: "Larteh Presbyterian Senior Secondary Technical School", type: "HighSchool" },
      { name: "Larteh Anglican Junior Secondary School", type: "MiddleSchool" }
    ],
    description: "Quainoo Emmanuel Asiedu Newton is a distinguished Ghanaian educator, entrepreneur, and visionary leader. Born on March 27, 1980, in Ghana, he is the founder of EduLegalHub, Asomas, and Court Connect. With decades of experience in education, he is dedicated to bridging the gap between education and legal services in Ghana through innovative technology platforms that empower teachers and provide accessible legal document generation for all Ghanaians.",
    expertise: ["Education Technology", "Legal Document Generation", "Teaching & Pedagogy", "Entrepreneurship", "Ghana Education System", "Curriculum Development", "EdTech Innovation"],
    awards: ["Pioneer in Ghana EdTech", "Educational Excellence Advocate"],
    socialProfiles: {
      website: "https://edumansystem.com/about",
      linkedin: "#",
      twitter: "#"
    }
  };

  // Enhanced JSON-LD Structured Data for Person (Founder) - More comprehensive
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://edumansystem.com/about#founder",
    "name": founderInfo.name,
    "alternateName": founderInfo.alternateName,
    "givenName": "Quainoo Emmanuel Asiedu",
    "familyName": "Newton",
    "birthDate": founderInfo.birthDate,
    "birthPlace": {
      "@type": "Place",
      "name": "Ghana",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "GH"
      }
    },
    "nationality": {
      "@type": "Country",
      "name": "Ghana",
      "sameAs": "https://en.wikipedia.org/wiki/Ghana"
    },
    "jobTitle": founderInfo.jobTitle,
    "description": founderInfo.description,
    "image": {
      "@type": "ImageObject",
      "url": founderInfo.image,
      "width": 800,
      "height": 800,
      "caption": "Quainoo Emmanuel Asiedu Newton - Founder & CEO of EduLegalHub"
    },
    "url": "https://edumansystem.com/about",
    "mainEntityOfPage": "https://edumansystem.com/about",
    "sameAs": [
      "https://edumansystem.com/about",
      "https://edumansystem.com"
    ],
    "worksFor": founderInfo.organizations.map(org => ({
      "@type": "Organization",
      "name": org,
      "url": org === "EduLegalHub" ? "https://edumansystem.com" : undefined
    })),
    "alumniOf": founderInfo.education.map(edu => ({
      "@type": edu.type,
      "name": edu.name,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "Ghana"
      }
    })),
    "knowsAbout": founderInfo.expertise,
    "hasOccupation": {
      "@type": "Occupation",
      "name": "Teaching Professional",
      "occupationLocation": {
        "@type": "Country",
        "name": "Ghana"
      }
    }
  };

  // Enhanced JSON-LD Structured Data for Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://edumansystem.com/#organization",
    "name": "EduLegalHub",
    "legalName": "EduLegalHub Ghana",
    "url": "https://edumansystem.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_edudoc-platform/artifacts/e62addr2_WhatsApp%20Image%202025-11-23%20at%208.14.18%20AM.jpeg",
      "width": 512,
      "height": 512
    },
    "description": "Ghana's leading platform for legal document generation and educational resources. EduLegalHub bridges the gap between complex legal processes and everyday people while empowering educators with quality teaching resources.",
    "slogan": "Bridging Education and Legal Services in Ghana",
    "founder": {
      "@type": "Person",
      "@id": "https://edumansystem.com/about#founder",
      "name": founderInfo.name,
      "image": founderInfo.image
    },
    "foundingDate": "2024",
    "foundingLocation": {
      "@type": "Place",
      "name": "Ghana"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Ghana"
    },
    "serviceType": ["Legal Document Generation", "Educational Resources", "Lesson Notes Platform"],
    "sameAs": [
      "https://edumansystem.com"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": "https://edumansystem.com/contact"
    }
  };

  // Enhanced JSON-LD for AboutPage with BreadcrumbList
  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://edumansystem.com/about#webpage",
    "name": "About EduLegalHub - Quainoo Emmanuel Asiedu Newton, Founder & CEO",
    "description": founderInfo.description,
    "url": "https://edumansystem.com/about",
    "mainEntity": {
      "@id": "https://edumansystem.com/about#founder"
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "EduLegalHub",
      "url": "https://edumansystem.com"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://edumansystem.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "About",
          "item": "https://edumansystem.com/about"
        }
      ]
    }
  };

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who is Quainoo Emmanuel Asiedu Newton?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Quainoo Emmanuel Asiedu Newton is a Ghanaian educator and entrepreneur, born on March 27, 1980. He is the founder of EduLegalHub, Asomas, and Court Connect, dedicated to bridging education and legal services in Ghana through innovative technology."
        }
      },
      {
        "@type": "Question",
        "name": "What is EduLegalHub?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "EduLegalHub is Ghana's leading platform for legal document generation and educational resources. It provides AI-powered legal document creation and quality lesson notes for teachers across Ghana."
        }
      },
      {
        "@type": "Question",
        "name": "Where did Quainoo Emmanuel Asiedu Newton study?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Quainoo Emmanuel Asiedu Newton studied at Koforidua Technical University, Larteh Presbyterian Senior Secondary Technical School, and Larteh Anglican Junior Secondary School in Ghana."
        }
      }
    ]
  };

  // Social share handler
  const handleShare = (platform) => {
    const url = encodeURIComponent("https://edumansystem.com/about");
    const text = encodeURIComponent("Meet Quainoo Emmanuel Asiedu Newton, Founder of EduLegalHub - Ghana's leading platform for legal document generation and educational resources.");
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${encodeURIComponent("About Quainoo Emmanuel Asiedu Newton - EduLegalHub Founder")}&body=${text}%20${url}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  // Scroll to top on mount for better UX
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Enhanced SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags - Enhanced */}
        <title>About EduLegalHub | Quainoo Emmanuel Asiedu Newton - Founder & CEO | Ghana EdTech Leader</title>
        <meta name="title" content="About EduLegalHub | Quainoo Emmanuel Asiedu Newton - Founder & CEO | Ghana EdTech Leader" />
        <meta name="description" content="Meet Quainoo Emmanuel Asiedu Newton, founder of EduLegalHub, Asomas, and Court Connect. Born March 27, 1980 in Ghana. A visionary educator and entrepreneur revolutionizing education and legal services in Ghana through innovative technology." />
        <meta name="keywords" content="Quainoo Emmanuel Asiedu Newton, Emmanuel Asiedu Newton, Quainoo Newton, Emmanuel Newton, EduLegalHub founder, Ghana educator, Ghana entrepreneur, Koforidua Technical University, Larteh Presbyterian, Asomas founder, Court Connect founder, Ghana legal technology, Ghana education platform, EdTech Ghana, Ghana teacher resources, legal document generation Ghana" />
        <meta name="author" content="Quainoo Emmanuel Asiedu Newton" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://edumansystem.com/about" />
        
        {/* Open Graph / Facebook - Enhanced */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://edumansystem.com/about" />
        <meta property="og:title" content="Quainoo Emmanuel Asiedu Newton - Founder of EduLegalHub | Ghana EdTech Pioneer" />
        <meta property="og:description" content="Meet Quainoo Emmanuel Asiedu Newton, visionary Ghanaian educator and entrepreneur. Founder of EduLegalHub, Asomas, and Court Connect. Revolutionizing education and legal services in Ghana." />
        <meta property="og:image" content={founderInfo.image} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="800" />
        <meta property="og:image:alt" content="Quainoo Emmanuel Asiedu Newton - Founder & CEO of EduLegalHub Ghana" />
        <meta property="og:site_name" content="EduLegalHub" />
        <meta property="og:locale" content="en_GH" />
        <meta property="profile:first_name" content="Quainoo Emmanuel Asiedu" />
        <meta property="profile:last_name" content="Newton" />
        <meta property="profile:username" content="quainoonewton" />
        
        {/* Twitter - Enhanced */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://edumansystem.com/about" />
        <meta name="twitter:title" content="Quainoo Emmanuel Asiedu Newton - Founder of EduLegalHub" />
        <meta name="twitter:description" content="Meet Quainoo Emmanuel Asiedu Newton, Ghanaian educator & entrepreneur. Founder of EduLegalHub, Asomas, Court Connect. Born March 27, 1980." />
        <meta name="twitter:image" content={founderInfo.image} />
        <meta name="twitter:creator" content="@edulegalhub" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GH" />
        <meta name="geo.country" content="Ghana" />
        <meta name="geo.placename" content="Ghana" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        <meta name="coverage" content="Ghana" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="True" />
        <meta name="MobileOptimized" content="320" />
        
        {/* Article specific for better indexing */}
        <meta property="article:author" content="Quainoo Emmanuel Asiedu Newton" />
        <meta property="article:published_time" content="2024-01-01T00:00:00+00:00" />
        <meta property="article:modified_time" content={new Date().toISOString()} />
        <meta property="article:section" content="About" />
        <meta property="article:tag" content="Quainoo Emmanuel Asiedu Newton" />
        <meta property="article:tag" content="EduLegalHub" />
        <meta property="article:tag" content="Ghana Education" />
        <meta property="article:tag" content="Legal Technology" />
        
        {/* JSON-LD Structured Data - Multiple schemas for comprehensive coverage */}
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(aboutPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header with Breadcrumb for SEO */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2" aria-label="EduLegalHub Home">
              <img 
                src="https://customer-assets.emergentagent.com/job_legaltech-ghana/artifacts/2u2cnqb9_file_000000007aec61f8982bd709fec16f87%20%281%29.png"
                alt="EduLegalHub Logo"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-8" aria-label="Main navigation">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                Back to Home
              </Link>
            </nav>
          </div>
          
          {/* Breadcrumb for SEO */}
          <nav className="mt-2" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-blue-600" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="text-gray-400">/</li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-gray-700 font-medium" itemProp="name">About</span>
                <meta itemProp="position" content="2" />
              </li>
            </ol>
          </nav>
        </div>
      </header>

      {/* Hero Section - Enhanced with semantic HTML */}
      <section className="py-16 px-4" aria-labelledby="about-heading">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-6">
              Our Story
            </Badge>
            <h1 id="about-heading" className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                About EduLegalHub
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              Bridging the gap between complex legal processes and everyday people in Ghana, 
              while empowering educators with quality teaching resources. We believe everyone 
              deserves access to legal assistance and excellent education.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Semantic markup */}
      <section className="pb-12 px-4" aria-labelledby="mission-vision-heading">
        <div className="container mx-auto">
          <h2 id="mission-vision-heading" className="sr-only">Our Mission and Vision</h2>
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              
              {/* Our Mission */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white" role="article">
                <CardHeader>
                  <CardTitle className="flex items-center text-blue-800">
                    <Target className="h-6 w-6 mr-3" aria-hidden="true" />
                    Our Mission
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    To democratize access to legal assistance and educational excellence in Ghana by providing 
                    AI-powered tools that simplify complex processes, making professional services accessible 
                    to everyone regardless of their background or location.
                  </CardDescription>
                  <ul className="space-y-3" role="list">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Simplify legal document creation for non-lawyers</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Provide quality lesson notes to enhance teaching</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Bridge the digital divide in education and legal services</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Our Vision */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-pink-50 to-white" role="article">
                <CardHeader>
                  <CardTitle className="flex items-center text-pink-800">
                    <Eye className="h-6 w-6 mr-3" aria-hidden="true" />
                    Our Vision
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    To become Ghana's leading platform where legal assistance meets educational excellence, 
                    creating a society where every individual can navigate legal processes confidently and 
                    every teacher has access to world-class educational resources.
                  </CardDescription>
                  <ul className="space-y-3" role="list">
                    <li className="flex items-start space-x-3">
                      <Star className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Leading innovation in legal-tech solutions</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Star className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Empowering educators across Ghana</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Star className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm text-gray-700">Building an informed and empowered society</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do - Enhanced semantic structure */}
      <section className="py-12 px-4 bg-white/50" aria-labelledby="services-heading">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="services-heading" className="text-3xl font-bold mb-4 text-gray-800">What We Do</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                EduLegalHub operates at the intersection of law and education, providing innovative solutions for two critical needs in Ghana
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              
              {/* Legal Services */}
              <Card className="border-0 shadow-lg" role="article">
                <CardHeader>
                  <CardTitle className="flex items-center text-blue-800">
                    <Gavel className="h-6 w-6 mr-3" aria-hidden="true" />
                    Legal Document Assistance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-gray-700 leading-relaxed">
                    We understand that not everyone can afford a lawyer, but everyone deserves access to proper legal documentation. 
                    Our AI-powered platform generates court-compliant legal documents in plain English, making justice accessible to all.
                  </CardDescription>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800">What We Offer:</h4>
                    <ul className="grid grid-cols-1 gap-2" role="list">
                      <li className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-blue-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Affidavits and sworn statements</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-blue-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Power of Attorney documents</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-blue-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Court applications and motions</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-blue-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Legal tutorials and guidance</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      <Lightbulb className="h-4 w-4 inline mr-1" aria-hidden="true" />
                      <strong>Our Approach:</strong> We translate complex legal language into simple, understandable terms, 
                      ensuring you understand every document you create.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Educational Services */}
              <Card className="border-0 shadow-lg" role="article">
                <CardHeader>
                  <CardTitle className="flex items-center text-pink-800">
                    <GraduationCap className="h-6 w-6 mr-3" aria-hidden="true" />
                    Educational Excellence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-gray-700 leading-relaxed">
                    Quality education is the foundation of societal progress. We support teachers across Ghana by providing 
                    professionally crafted lesson notes and educational resources that align with the Ghana Education Service curriculum.
                  </CardDescription>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800">What We Provide:</h4>
                    <ul className="grid grid-cols-1 gap-2" role="list">
                      <li className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4 text-pink-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Weekly lesson notes for all levels</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4 text-pink-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Teacher dashboard and resource management</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4 text-pink-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Headteacher portal for school administration</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4 text-pink-500" aria-hidden="true" />
                        <span className="text-sm text-gray-700">Curriculum-aligned educational materials</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="text-pink-800 text-sm">
                      <Award className="h-4 w-4 inline mr-1" aria-hidden="true" />
                      <strong>Our Commitment:</strong> Every lesson note is crafted by educational experts and 
                      reviewed for quality, ensuring your students receive the best possible education.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 px-4" aria-labelledby="why-choose-heading">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="why-choose-heading" className="text-3xl font-bold mb-4 text-gray-800">Why Choose EduLegalHub?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We're more than just a platform – we're your partners in navigating life's legal and educational challenges
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              
              <Card className="border-0 shadow-lg text-center" role="article">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-blue-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">For Everyone</h3>
                  <p className="text-gray-600 text-sm">
                    Designed for non-legal professionals and teachers who need professional-quality services 
                    without the traditional barriers.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center" role="article">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="h-8 w-8 text-green-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Ghana-Focused</h3>
                  <p className="text-gray-600 text-sm">
                    Built specifically for Ghana's legal system and educational curriculum, 
                    ensuring relevance and compliance with local standards.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center" role="article">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lightbulb className="h-8 w-8 text-purple-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">AI-Powered</h3>
                  <p className="text-gray-600 text-sm">
                    Leveraging cutting-edge artificial intelligence to provide accurate, 
                    personalized solutions while maintaining human oversight and quality control.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-12 px-4 bg-gradient-to-r from-blue-600 to-pink-600" aria-labelledby="impact-heading">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 id="impact-heading" className="text-3xl font-bold mb-6">Our Impact in Ghana</h2>
            <p className="text-xl text-blue-100 leading-relaxed mb-8">
              Every legal document generated and every lesson note downloaded represents a life improved, 
              a case resolved, or a student better educated. We're proud to be part of Ghana's digital transformation.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold mb-2">1,000+</div>
                <div className="text-blue-100">Legal Documents Generated</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-blue-100">Teachers Empowered</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">50,000+</div>
                <div className="text-blue-100">Students Impacted</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Founder - Enhanced with Schema markup and better UX */}
      <section className="py-16 px-4 bg-white" aria-labelledby="founder-heading" itemScope itemType="https://schema.org/Person">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-4">
                Leadership
              </Badge>
              <h2 id="founder-heading" className="text-3xl font-bold text-gray-800">Meet the Founder</h2>
            </div>

            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="grid lg:grid-cols-5 gap-0">
                {/* Founder Image - Optimized with proper alt text and loading */}
                <div className="relative lg:col-span-2 h-80 lg:h-auto min-h-[400px]">
                  <img 
                    src={founderInfo.image}
                    alt="Quainoo Emmanuel Asiedu Newton - Founder and CEO of EduLegalHub, Ghana educator and entrepreneur"
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                    itemProp="image"
                    width="400"
                    height="500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 lg:hidden">
                    <h3 className="text-2xl font-bold text-white" itemProp="name">Quainoo Emmanuel Asiedu Newton</h3>
                    <p className="text-blue-200" itemProp="jobTitle">Founder & CEO</p>
                  </div>
                  
                  {/* Social Share Buttons - For off-page SEO */}
                  <div className="absolute top-4 right-4 flex flex-col space-y-2">
                    <button 
                      onClick={() => handleShare('linkedin')}
                      className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                      aria-label="Share on LinkedIn"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="h-4 w-4 text-blue-700" />
                    </button>
                    <button 
                      onClick={() => handleShare('twitter')}
                      className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                      aria-label="Share on Twitter"
                      title="Share on Twitter"
                    >
                      <Twitter className="h-4 w-4 text-blue-400" />
                    </button>
                    <button 
                      onClick={() => handleShare('facebook')}
                      className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                      aria-label="Share on Facebook"
                      title="Share on Facebook"
                    >
                      <Facebook className="h-4 w-4 text-blue-600" />
                    </button>
                  </div>
                </div>

                {/* Founder Details - Enhanced semantic markup */}
                <CardContent className="lg:col-span-3 p-8 lg:p-10 bg-gradient-to-br from-gray-50 to-white">
                  <div className="hidden lg:block mb-6">
                    <h3 className="text-2xl font-bold text-gray-800" itemProp="name">Quainoo Emmanuel Asiedu Newton</h3>
                    <p className="text-blue-600 font-medium" itemProp="jobTitle">Founder & CEO</p>
                    <meta itemProp="alternateName" content="Emmanuel Asiedu Newton" />
                    <meta itemProp="alternateName" content="Quainoo Newton" />
                  </div>

                  <div className="space-y-6">
                    {/* Key Info Badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span itemProp="nationality">Ghana</span>
                      </Badge>
                      <Badge className="bg-pink-50 text-pink-700 border-pink-200 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Born: <time itemProp="birthDate" dateTime="1980-03-27">March 27, 1980</time>
                      </Badge>
                      <Badge className="bg-green-50 text-green-700 border-green-200 flex items-center">
                        <Briefcase className="h-3 w-3 mr-1" />
                        <span itemProp="hasOccupation">Educator & Entrepreneur</span>
                      </Badge>
                    </div>

                    {/* Bio - Optimized for featured snippets */}
                    <p className="text-gray-700 leading-relaxed" itemProp="description">
                      <strong>Quainoo Emmanuel Asiedu Newton</strong> is a visionary Ghanaian educator and entrepreneur who founded 
                      <strong> EduLegalHub</strong> with a mission to bridge the gap between education and legal services in Ghana. 
                      Born on <time dateTime="1980-03-27"><strong>March 27, 1980</strong></time>, his passion for teaching and 
                      dedication to empowering communities has driven him to create innovative platforms including 
                      <strong> Asomas</strong> and <strong>Court Connect</strong> that serve both educators and the general public.
                    </p>

                    {/* Education - Structured for SEO */}
                    <div itemProp="alumniOf" itemScope itemType="https://schema.org/EducationalOrganization">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <GraduationCap className="h-5 w-5 mr-2 text-blue-600" aria-hidden="true" />
                        Educational Background
                      </h4>
                      <ul className="space-y-2 pl-7" role="list">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-sm text-gray-700" itemProp="name">Koforidua Technical University</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-sm text-gray-700">Larteh Presbyterian Senior Secondary Technical School</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-sm text-gray-700">Larteh Anglican Junior Secondary School</span>
                        </li>
                      </ul>
                    </div>

                    {/* Enterprises - Schema markup for organizations */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <Award className="h-5 w-5 mr-2 text-pink-600" aria-hidden="true" />
                        Founded Enterprises
                      </h4>
                      <div className="pl-7">
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200" itemProp="worksFor" itemScope itemType="https://schema.org/Organization">
                            <span itemProp="name">EduLegalHub</span>
                          </Badge>
                          <Badge className="bg-pink-100 text-pink-700 border-pink-200" itemProp="worksFor" itemScope itemType="https://schema.org/Organization">
                            <span itemProp="name">Asomas</span>
                          </Badge>
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200" itemProp="worksFor" itemScope itemType="https://schema.org/Organization">
                            <span itemProp="name">Court Connect</span>
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Expertise Tags - For SEO keywords */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <Lightbulb className="h-5 w-5 mr-2 text-yellow-600" aria-hidden="true" />
                        Areas of Expertise
                      </h4>
                      <div className="flex flex-wrap gap-2 pl-7">
                        {founderInfo.expertise.slice(0, 5).map((skill, index) => (
                          <span 
                            key={index}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                            itemProp="knowsAbout"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Quote - Engaging UX element */}
                    <blockquote className="bg-gradient-to-r from-blue-50 to-pink-50 p-4 rounded-lg border-l-4 border-blue-500">
                      <p className="text-gray-700 italic text-sm">
                        "Education and justice are the pillars of a progressive society. Through technology, 
                        we can make both accessible to everyone, regardless of their circumstances."
                      </p>
                      <footer className="text-gray-500 text-xs mt-2">— Quainoo Emmanuel Asiedu Newton</footer>
                    </blockquote>

                    {/* Share Profile CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Share2 className="h-4 w-4 mr-1" />
                        Share this profile
                      </span>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleShare('linkedin')}
                          className="text-gray-400 hover:text-blue-700 transition-colors"
                          aria-label="Share on LinkedIn"
                        >
                          <Linkedin className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleShare('twitter')}
                          className="text-gray-400 hover:text-blue-400 transition-colors"
                          aria-label="Share on Twitter"
                        >
                          <Twitter className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleShare('facebook')}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          aria-label="Share on Facebook"
                        >
                          <Facebook className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleShare('email')}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label="Share via Email"
                        >
                          <Mail className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4" aria-labelledby="cta-heading">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto">
            <h2 id="cta-heading" className="text-3xl font-bold mb-4 text-gray-800">Ready to Get Started?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Join thousands of Ghanaians who trust EduLegalHub for their legal and educational needs. 
              Whether you need legal documents or teaching resources, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-8 py-4 font-semibold"
                asChild
              >
                <Link to="/document-generator">
                  <FileText className="mr-2 h-5 w-5" aria-hidden="true" />
                  Create Legal Documents
                </Link>
              </Button>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 px-8 py-4 font-semibold"
                asChild
              >
                <Link to="/">
                  <BookOpen className="mr-2 h-5 w-5" aria-hidden="true" />
                  Access Lesson Notes
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="pb-12 px-4" aria-labelledby="contact-cta-heading">
        <div className="container mx-auto">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-white max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <Heart className="h-12 w-12 text-red-500 mx-auto mb-4" aria-hidden="true" />
              <h3 id="contact-cta-heading" className="text-2xl font-semibold mb-4 text-gray-800">Have Questions?</h3>
              <p className="text-gray-600 mb-6">
                We'd love to hear from you! Whether you have questions, suggestions, or need support, 
                our team is here to help you succeed.
              </p>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-gray-300 px-8 py-3 font-semibold"
                asChild
              >
                <Link to="/contact">
                  Get in Touch
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
